<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useCreditMessagesReady } from '../composables/useCreditMessagesReady'
import { TRIAL_EXPIRING_DAYS } from '../service/creditPresentation'
import { useCreditStore } from '../stores/creditStore'

import { useAccount } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui'

const DISMISS_PREFIX = 'orphex.credit-trial-banner'
const DAYS_LEFT_KEY = 'credits.slot.daysLeft'

const store = useCreditStore()
const { accountId } = useAccount()
const { t } = useI18n()
const dismissed = ref(false)
const messagesReady = useCreditMessagesReady(DAYS_LEFT_KEY)

function dayKey(date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const dismissalKey = computed(() => {
  const id = accountId.value
  return id === undefined ? null : `${DISMISS_PREFIX}:${String(id)}:${dayKey()}`
})

function readDismissed(): void {
  const key = dismissalKey.value
  if (!key) {
    dismissed.value = false
    return
  }
  try {
    dismissed.value = window.localStorage.getItem(key) === '1'
  } catch {
    dismissed.value = false
  }
}

watch(dismissalKey, readDismissed, { immediate: true })

const visible = computed(() => {
  const days = store.trialDaysRemaining
  return (
    !dismissed.value &&
    messagesReady.value &&
    store.isTrial &&
    days !== null &&
    days >= 0 &&
    days <= TRIAL_EXPIRING_DAYS
  )
})

function dismiss(): void {
  dismissed.value = true
  const key = dismissalKey.value
  if (!key) return
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    // Storage can be denied; the in-memory dismissal still prevents repeat noise this mount.
  }
}
</script>

<template>
  <Alert
    v-if="visible"
    tone="warning"
    closable
    :title="t('credits.panel.freeTrial')"
    :close-label="t('common.close')"
    data-testid="trial-banner"
    @close="dismiss"
  >
    {{ t(DAYS_LEFT_KEY, store.trialDaysRemaining ?? 0) }}
  </Alert>
</template>
